// src/components/common/avatar/display/AvatarDisplay.tsx


"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";

import type { RootState } from "../../../../redux/storeTypes";
import { selectPresenceOnlineUsersMap } from "../../../../redux/selectors/presence/presenceSelectors";
import { getDefaultAvatar } from "../../../../utils/helpers/avatarFallback";

import { AvatarSkeleton } from "../../index";
import VerificationBadge from "../../icons/verification/VerificationBadge";

import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";
import type { PrivateCdnTarget } from "@/asset-delivery/components/PrivateCdnImage";

import "./AvatarDisplay.css";

type AvatarShape = "circle" | "rounded" | "square";
type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "mega";

interface AvatarDisplayProps {
  userId?: number;

  /**
   * NEW:
   * - user: avatar_cdn_url
   * - group: group_avatar_cdn_url
   */
  src?: string | null;

  /**
   * Optional legacy proxy url:
   * - user: avatar_url
   * - group: group_avatar_url
   */
  placeholderSrc?: string | null;

  alt?: string;
  shape?: AvatarShape;
  size?: AvatarSize;
  onClick?: () => void;
  withBorder?: boolean;
  borderColor?: string;
  className?: string;
  title?: string;
  type?: "user" | "group";
  isVerifiedIdentity?: boolean;
  isTownlitVerified?: boolean;
  isOnline?: boolean;

  /** Warm earlier before entering viewport. */
  rootMargin?: string;

  /** Use only for critical above-the-fold avatars. */
  priority?: boolean;
}

const LOAD_TIMEOUT_MS = 2500;

/* -------------------------------------------------
 * Helpers
 * ------------------------------------------------- */
const isLocalLike = (url?: string | null) => {
  if (!url) return false;
  const u = String(url).trim();
  return u.startsWith("/") || u.startsWith("data:") || u.startsWith("blob:");
};

const isDefaultAvatar = (url?: string | null) => {
  if (!url) return false;
  return url.includes("/defaults/") || url.includes("default");
};

/* -------------------------------------------------
 * Component
 * ------------------------------------------------- */
const AvatarDisplay: React.FC<AvatarDisplayProps> = ({
  userId,
  src = null,
  placeholderSrc = null,
  alt = "Avatar",
  shape = "circle",
  size = "md",
  onClick,
  withBorder = false,
  borderColor,
  className = "",
  title = "",
  type = "user",
  isVerifiedIdentity = false,
  isTownlitVerified = false,
  isOnline: propOnline,
  rootMargin = "900px",
  priority = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ----------------------------
  // ONLINE STATUS
  // Only user avatars can display presence.
  // Group avatars must never use user presence by object_id.
  // ----------------------------
  const canShowPresence = type === "user" && typeof userId === "number";

  const isOnlineFromRedux = useSelector(
    (state: RootState) =>
      canShowPresence
        ? Boolean(selectPresenceOnlineUsersMap(state)[userId])
        : false,
    shallowEqual
  );

  const isOnline = canShowPresence ? propOnline ?? isOnlineFromRedux : false;

  // ----------------------------
  // BADGE
  // ----------------------------
  const badgeType = useMemo<"none" | "identity" | "townlit">(() => {
    if (isVerifiedIdentity && isTownlitVerified) return "townlit";
    if (isVerifiedIdentity) return "identity";
    return "none";
  }, [isVerifiedIdentity, isTownlitVerified]);

  const badgeVariant =
    badgeType === "townlit" ? "gold" : badgeType === "identity" ? "blue" : null;

  // ----------------------------
  // URLS
  // ----------------------------
  const cleanSrc = useMemo(() => {
    if (!src || typeof src !== "string") return null;
    const t = src.trim();
    return t ? t : null;
  }, [src]);

  const proxySrc = useMemo(() => {
    if (!placeholderSrc || typeof placeholderSrc !== "string") return null;
    const t = placeholderSrc.trim();
    return t ? t : null;
  }, [placeholderSrc]);

  // Only local fallback is allowed after failure.
  const fallbackSrc = getDefaultAvatar(type);

  // Avoid duplicate image requests.
  const safeProxySrc = useMemo(() => {
    if (!proxySrc) return null;
    if (proxySrc === cleanSrc) return null;
    return proxySrc;
  }, [proxySrc, cleanSrc]);

  // Skip warmup for local/blob/data sources.
  const skipWarmup = useMemo(() => {
    return isLocalLike(cleanSrc) || (!cleanSrc && isLocalLike(safeProxySrc));
  }, [cleanSrc, safeProxySrc]);

  // ----------------------------
  // BUILD PRIVATE CDN TARGET
  // ----------------------------
  const privateTarget = useMemo<PrivateCdnTarget | null>(() => {
    if (typeof userId !== "number") return null;

    if (type === "user") {
      return {
        app_label: "accounts",
        model: "customuser",
        object_id: userId,
      };
    }

    if (type === "group") {
      return {
        app_label: "conversation",
        model: "dialogue",
        object_id: userId,
      };
    }

    return null;
  }, [type, userId]);

  // Backend-resolved avatar field.
  const fieldName = type === "group" ? "group_image" : "image_name";

  // ----------------------------
  // LOADING RESET
  // IMPORTANT: do not include hasError here.
  // ----------------------------
  const imageIdentityKey = useMemo(() => {
    return `${type}:${userId ?? "x"}:${fieldName}:${
      cleanSrc ?? "no-src"
    }:${safeProxySrc ?? "no-proxy"}`;
  }, [type, userId, fieldName, cleanSrc, safeProxySrc]);

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, LOAD_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [imageIdentityKey]);

  // ----------------------------
  // Image events
  // ----------------------------
  const handleLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsLoading(false);
  };

  const handleError = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Prevent repeated error state updates.
    setHasError((prev) => (prev ? prev : true));
    setIsLoading(false);
  };

  // ----------------------------
  // Private image eligibility
  // ----------------------------
  const hasRealPrivateSrc =
    Boolean(cleanSrc) && !isDefaultAvatar(cleanSrc);

  const canUsePrivate =
    !hasError &&
    hasRealPrivateSrc &&
    Boolean(privateTarget) &&
    typeof userId === "number";

  // Never pass fallback through PrivateCdnImage.
  const finalSrc = hasError ? fallbackSrc : cleanSrc;
  const finalPlaceholder = hasError ? null : safeProxySrc;

  const imageLoading = priority ? "eager" : "lazy";
  const imageFetchPriority = priority ? "high" : "auto";

  return (
    <div
      className={`avatar-display-wrapper ${shape} ${size} ${className} ${
        isOnline ? "online" : ""
      }`}
      title={title}
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
    >
      {badgeVariant && (
        <div className={`avatar-verified-badge badge-${size}`}>
          <VerificationBadge variant={badgeVariant} size={size} />
        </div>
      )}

      <div
        className={`avatar-display ${shape} ${size} ${
          withBorder ? "with-border" : ""
        }`}
        style={{
          borderColor: withBorder && borderColor ? borderColor : undefined,
        }}
      >
        {isLoading && (
          <div className="avatar-loader-overlay">
            <AvatarSkeleton
              fitParent
              shape={shape}
              size={size}
              withBorder={withBorder}
              ringColor={borderColor}
              showBadge={badgeType !== "none"}
              badgeSize={size}
            />
          </div>
        )}

        {canUsePrivate && privateTarget ? (
          <PrivateCdnImage
            src={finalSrc || null}
            placeholderSrc={finalPlaceholder}
            alt={alt}
            className="avatar-img"
            imgClassName="avatar-img"
            target={privateTarget}
            kind="image"
            fieldName={fieldName}
            eager={priority}
            rootMargin={rootMargin}
            threshold={0.01}
            skipWarmup={skipWarmup}
            failOpen
            imgLoading={imageLoading}
            fetchPriority={imageFetchPriority}
            onLoad={handleLoad}
            onError={handleError}
          />
        ) : (
          <img
            src={finalSrc || finalPlaceholder || fallbackSrc}
            alt={alt}
            className="avatar-img"
            loading={imageLoading}
            decoding="async"
            draggable={false}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </div>
    </div>
  );
};

export default AvatarDisplay;